/**
 * @fileoverview Shopping Cart Component
 *
 * This file contains the main ShoppingCart component that provides a comprehensive
 * shopping cart interface with both cart view and checkout functionality.
 *
 * Key Features:
 * - Cart item management (view, update quantities, remove items)
 * - Checkout workflow integration with form validation
 * - Order placement with proper error handling
 * - Toast notifications for user feedback
 * - Responsive overlay interface
 * - Body scroll management when open
 * - Imperative API for programmatic control
 *
 * The component leverages multiple custom hooks for clean separation of concerns:
 * - useCart: Cart state management via React Context
 * - useLocalCartItems: Local state for immediate UI updates
 * - useCartToast: Toast notification management
 * - useCheckoutManagement: Checkout workflow state and operations
 * - useOrderPlacement: Order submission logic
 * - useItemManagement: Item quantity and removal operations
 */

import React, { useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import classNames from "classnames";
import { ShoppingItems } from "./ShoppingItems";
import { CheckoutForm } from "./CheckoutForm";
import { CheckoutFormRef } from "./CheckoutForm/CheckoutForm";
import {
  calculateTotal,
  manageBodyScroll,
  createLoadingButtonContent,
  getCurrencySymbol,
} from "./ShoppingCart.utils";
import {
  useLocalCartItems,
  useCartToast,
  useCheckoutManagement,
  useOrderPlacement,
  useItemManagement,
} from "./ShoppingCart.hooks";
import { useCart } from "./CartContext";
import { ShoppingCartRef, ShoppingCartComponentProps } from "./ShoppingCart.types";
import ToastMessage from "components/ToastMessages/ToastMessage";
import { SHOP_CONFIG } from "pages/Shop/ShopList.config";
import "./ShoppingCart.scss";

// Re-export types for external use
export type { ShoppingCartRef, ShoppingCartComponentProps };

/**
 * ShoppingCart Component
 *
 * A comprehensive shopping cart component that handles both cart view and checkout flow.
 * Features include:
 * - Cart item management (add, remove, update quantities)
 * - Checkout form integration
 * - Order placement
 * - Toast notifications
 * - Responsive UI with loading states
 *
 * The component uses several custom hooks to manage different aspects:
 * - useCart: Cart state from context
 * - useLocalCartItems: Local cart state for immediate UI updates
 * - useCartToast: Toast notification management
 * - useCheckoutManagement: Checkout flow state and operations
 * - useOrderPlacement: Order submission logic
 * - useItemManagement: Item quantity and removal operations
 */
const ShoppingCart = forwardRef<ShoppingCartRef, ShoppingCartComponentProps>(
  ({ onClose, onResetCart }, ref) => {
    // Cart context and state
    const {
      isOpen,
      items,
      loading,
      error,
      openCart,
      closeCart,
      toggleCart,
      refreshCart,
      updateItemsFromCheckoutData,
    } = useCart();

    // Local cart items for immediate UI updates
    const { localItems, updateLocalQuantity } = useLocalCartItems(items);

    // Toast notifications
    const { toast, showToast, hideToast } = useCartToast(SHOP_CONFIG.TOAST_DURATION);

    // Checkout management
    const {
      mode,
      checkoutData,
      checkoutLoading,
      checkoutError,
      checkoutTotal,
      currentFormData,
      resetCheckoutState,
      handleCheckout,
      handleCancelCheckout,
      handleTotalUpdate,
      handleFormDataChange,
    } = useCheckoutManagement(refreshCart, updateItemsFromCheckoutData, showToast);

    // Form reference for validation
    const checkoutFormRef = useRef<CheckoutFormRef>(null);

    // Order placement
    const { isPlacingOrder, handlePlaceOrder } = useOrderPlacement(
      checkoutData,
      currentFormData,
      checkoutFormRef,
      showToast,
      async () => {
        handleClose();
        await refreshCart();
      }
    );

    // Item management
    const { localIsUpdating, isUpdating, handleQuantityChange, handleRemoveItem } =
      useItemManagement(items, localItems, updateLocalQuantity, refreshCart, showToast);

    /**
     * Imperative handle for parent component control
     */
    useImperativeHandle(ref, () => ({
      open: async () => {
        if (isOpen && mode === "checkout") {
          // If cart is open in checkout mode, switch back to cart mode and refresh
          resetCheckoutState();
          await refreshCart();
        } else {
          openCart();
        }
      },
      close: closeCart,
      toggle: async () => {
        if (isOpen) {
          if (mode === "checkout") {
            // If cart is open in checkout mode, switch back to cart mode instead of closing
            resetCheckoutState();
            await refreshCart();
          } else {
            // If cart is open in cart mode, close it normally
            toggleCart();
          }
        } else {
          // If cart is closed, open it normally
          toggleCart();
        }
      },
    }));

    /**
     * Handles cart close with proper cleanup
     */
    const handleClose = () => {
      closeCart();
      hideToast();
      resetCheckoutState();
      onClose?.();
    };

    /**
     * Handles overlay clicks (only closes in cart mode)
     */
    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && mode === "cart") {
        handleClose();
      }
    };

    // Prevent body scroll when cart is open
    useEffect(() => {
      const cleanup = manageBodyScroll(isOpen);
      return cleanup;
    }, [isOpen]);

    // Calculate totals and item counts
    const calculatedTotal = calculateTotal(localItems);
    const total = mode === "checkout" && checkoutTotal !== null ? checkoutTotal : calculatedTotal;
    const hasItems = localItems.length > 0;
    const currencySymbol = getCurrencySymbol(localItems, checkoutData);

    return (
      <>
        {/* Overlay */}
        <div
          data-testid="cart-overlay"
          className={classNames("ShoppingCartOverlay", { open: isOpen })}
          onClick={handleOverlayClick}
        />

        {/* Main cart panel */}
        <div
          data-testid="cart-panel"
          className={classNames("ShoppingCartPanel", {
            open: isOpen,
            checkout: mode === "checkout",
          })}
        >
          {/* Header */}
          <div className="CartHeader">
            <h2 className="CartTitle">{mode === "cart" ? "Shopping Basket" : "Checkout"}</h2>
            <button
              data-testid="cart-close"
              className="CloseButton"
              onClick={handleClose}
              aria-label="Close cart"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className={classNames("CartContent", { checkout: mode === "checkout" })}>
            {/* Checkout Form (only in checkout mode) */}
            {mode === "checkout" && (
              <div className="CheckoutFormContainer">
                <CheckoutForm
                  ref={checkoutFormRef}
                  onTotalUpdate={handleTotalUpdate}
                  onFormDataChange={handleFormDataChange}
                  checkoutData={checkoutData}
                  loading={checkoutLoading}
                  error={checkoutError}
                />
              </div>
            )}

            {/* Cart Items */}
            <div className={classNames("CartItems", { checkout: mode === "checkout" })}>
              <div className="ShoppingItemsList">
                <ShoppingItems
                  items={localItems}
                  loading={loading}
                  error={error}
                  onUpdateQuantity={handleQuantityChange}
                  onRemoveItem={handleRemoveItem}
                  onResetCart={onResetCart}
                  onRetry={refreshCart}
                  readonly={mode === "checkout"}
                />
              </div>

              {/* Checkout Footer */}
              {hasItems && !loading && mode === "checkout" && (
                <div className="CartFooter">
                  <div className="TotalSection">
                    <p className="TotalLabel">Total:</p>
                    <p className="TotalAmount">
                      {currencySymbol}
                      {total.toFixed(2)}
                    </p>
                  </div>

                  <div className="CheckoutButtonGroup">
                    <button
                      className="CancelButton"
                      data-testid="cart-cancel-btn"
                      onClick={handleCancelCheckout}
                    >
                      Cancel
                    </button>
                    <button
                      className="PlaceOrderButton"
                      data-testid="cart-place-order-btn"
                      onClick={handlePlaceOrder}
                      disabled={!hasItems || isUpdating || localIsUpdating || isPlacingOrder}
                    >
                      {isPlacingOrder
                        ? createLoadingButtonContent("Placing Order...")
                        : "Place Order"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cart Footer (cart mode only) */}
          {hasItems && !loading && mode === "cart" && (
            <div className="CartFooter">
              <div className="TotalSection">
                <p className="TotalLabel">Total:</p>
                <p className="TotalAmount">
                  {currencySymbol}
                  {total.toFixed(2)}
                </p>
              </div>

              <button
                className="CheckoutButton"
                data-testid="cart-checkout-btn"
                onClick={handleCheckout}
                disabled={!hasItems || isUpdating || localIsUpdating}
              >
                {isUpdating || localIsUpdating
                  ? createLoadingButtonContent("Updating...")
                  : "Checkout"}
              </button>
            </div>
          )}
        </div>

        {/* Toast Messages */}
        {toast && <ToastMessage title={toast.title} text={toast.text} bSuccess={toast.bSuccess} />}
      </>
    );
  }
);

ShoppingCart.displayName = "ShoppingCart";

export default ShoppingCart;
